import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarClock,
  Download,
  Plus,
  Search,
  ShieldAlert,
  Upload,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { errorCodeOf } from '../../services/api-error';
import {
  registrationsService,
  type BulkPayload,
  type ImportResult,
  type Registration,
  type RegistrationCandidate,
  type RegistrationCategory,
  type RegistrationTeam,
} from '../../services/registrations';
import EntrantDialog from '../components/EntrantDialog';
import ImportEntriesDialog from '../components/ImportEntriesDialog';
import RegistrationsTable from '../components/RegistrationsTable';
import { downloadEntries } from '../entry-sheet';
import { useGymAdmin } from '../context/gym-admin-context';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

/** The squad filter's two values that are not a squad. */
const ALL_TEAMS = 'all';
const NO_TEAM = 'none';

/**
 * One stage's entry list: who is climbing, in which category, and who has paid.
 *
 * Entrants come from the circuit's roster — entered once for the season, signed
 * up stage by stage — so this page never asks for a name. It picks an id, and
 * the server stamps the identity from the roster row.
 *
 * The list is laid out the way the day is run: one table per category, with
 * whoever is not sorted yet at the top. Each table's header works on the rows
 * ticked under it, because a desk sorts a competition in batches — a club that
 * paid together, a coach's whole squad — not one entrant at a time.
 */
export default function RegistrationsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: stage, loading: stageLoading, slug } = useCurrentLocation();
  const { place } = useGymAdmin();

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  // One squad's rows at a time: "what did Boulder Lab enter", which is the
  // question once squads enter their own athletes from the competition's page.
  const [teamFilter, setTeamFilter] = useState<string>(ALL_TEAMS);
  const [categories, setCategories] = useState<RegistrationCategory[]>([]);
  const [teams, setTeams] = useState<RegistrationTeam[]>([]);
  const [paidCount, setPaidCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [dpaRequired, setDpaRequired] = useState(false);
  // What the entry form asks here, as the list says: the class's gender where
  // the organiser's agreement covers it, and "automatic" where a strategy
  // gives the classes. And whether any class is split over turns to book.
  const [genderCollected, setGenderCollected] = useState(false);
  const [assignsCategories, setAssignsCategories] = useState(false);
  const [turnsOffered, setTurnsOffered] = useState(false);
  // Only the rows whose class is split over two or more turns and that have
  // none booked: the desk's other to-do list on the morning, beside the
  // unsorted table.
  const [turnFilter, setTurnFilter] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  // Whether this viewer may still add and change entries at all — the desk
  // closes with the competition for everyone but an admin.
  const [deskOpen, setDeskOpen] = useState(true);
  // Whether the competition takes an entry fee at all. Opt-in on the Info
  // page: a free one has no "paid" column, no count and no bulk action for it.
  const [acceptsPayments, setAcceptsPayments] = useState(false);
  // Taking somebody on at the desk, and correcting an entry this competition
  // owns. Both go through the same form, and both live in the URL: `?entrant=`
  // is an entry's id, or `new`. A desk works two to a table and passes rows to
  // each other — "open Bianchi and fix her tax code" has to be a link, and a
  // reloaded page has to come back to the row it was on rather than to the
  // top of the list.
  const [searchParams, setSearchParams] = useSearchParams();
  const entrantParam = searchParams.get('entrant');
  // A club's list in Excel, or this list exported, corrected and sent back.
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<RegistrationCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  // A squad written from inside the entry form joins the list the pickers and
  // the tables read, so it is there for the next entrant without a reload.
  const addTeam = useCallback((team: RegistrationTeam) => {
    setTeams((current) =>
      current.some((existing) => existing.id === team.id)
        ? current
        : [ ...current, team ].sort((a, b) => a.name.localeCompare(b.name))
    );
  }, []);

  const load = useCallback(
    async (stageSlug: string) => {
      setListLoading(true);
      try {
        const page = await registrationsService.list(stageSlug);
        setRegistrations(page.registrations);
        setCategories(page.categories);
        setTeams(page.teams);
        setPaidCount(page.paid);
        setConfirmedCount(page.confirmed);
        setDeskOpen(page.deskOpen);
        setAcceptsPayments(page.acceptsPayments);
        setGenderCollected(page.genderCollected);
        setAssignsCategories(page.assignsCategories);
        setTurnsOffered(page.turnsOffered);
        setDpaRequired(false);
      } catch (error) {
        // Not signing the Art. 28 agreement is not a failure to load: the
        // server is refusing to serve a list drawn from a roster it has no
        // contract to hold, and the admin needs the way out rather than a toast.
        if (errorCodeOf(error) === 'dpa_required') {
          setDpaRequired(true);
          return;
        }
        toast({
          title: t('gymAdmin.registrations.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  // The roster search runs server-side; debounced so typing a surname is one
  // request rather than eight.
  useEffect(() => {
    if (!pickerOpen || !slug) return;
    setCandidatesLoading(true);
    const timer = setTimeout(() => {
      registrationsService
        .candidates(slug, search || undefined)
        .then(setCandidates)
        .catch(() => setCandidates([]))
        .finally(() => setCandidatesLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [pickerOpen, search, slug]);

  const refreshCounts = (next: Registration[]) => {
    setRegistrations(next);
    setPaidCount(next.filter((r) => r.paid).length);
    setConfirmedCount(next.filter((r) => r.confirmed).length);
  };

  const reportError = (error: unknown) =>
    toast({
      title: t('gymAdmin.registrations.saveError'),
      description: error instanceof Error ? error.message : t('errors.unexpectedError'),
      variant: 'destructive',
    });

  const handleAdd = async (candidate: RegistrationCandidate) => {
    if (!slug) return;
    try {
      // Picking somebody already known copies their details in: from their
      // entry at another stage of the circuit, or from the hosting gym's roster.
      const created = await registrationsService.create(
        slug,
        candidate.kind === 'member' ? { from_member_id: candidate.id } : { from_entry_id: candidate.id }
      );
      refreshCounts([...registrations, created]);
      setCandidates((current) => current.filter((c) => c.id !== candidate.id));
      toast({ title: t('gymAdmin.registrations.added', { name: created.full_name }) });
    } catch (error) {
      reportError(error);
    }
  };

  // Both of these rethrow: the control that was pressed reports its own
  // outcome (a status button returning to idle, a selection staying ticked),
  // and it can only do that if the failure reaches it.
  const togglePaid = async (registration: Registration) => {
    if (!slug) return;
    try {
      const updated = await registrationsService.update(slug, registration.id, {
        paid: !registration.paid,
      });
      refreshCounts(registrations.map((r) => (r.id === updated.id ? updated : r)));
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  // Confirming is the desk saying "this person is climbing": it is what puts
  // them on the scoreboard and lets a judge — or the athlete — record a go.
  const toggleConfirmed = async (registration: Registration) => {
    if (!slug) return;
    try {
      const updated = await registrationsService.update(slug, registration.id, {
        confirmed: !registration.confirmed,
      });
      refreshCounts(registrations.map((r) => (r.id === updated.id ? updated : r)));
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  const applyBulk = async (ids: string[], attributes: BulkPayload) => {
    if (!slug) return;
    try {
      const updated = await registrationsService.bulkUpdate(slug, ids, attributes);
      const byId = new Map(updated.map((r) => [r.id, r]));
      refreshCounts(registrations.map((r) => byId.get(r.id) ?? r));
      // A batch put on a turn takes seats on it: the turns' counts the
      // headers show are read again, quietly, without blanking the list.
      if ('competition_round_id' in attributes) {
        registrationsService
          .list(slug)
          .then((page) => setCategories(page.categories))
          .catch(() => {});
      }
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  /** Opens the entry form at its own URL — an entry's id, or `new`. */
  const openEntrant = (entrant: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (entrant) next.set('entrant', entrant);
    else next.delete('entrant');
    setSearchParams(next, { replace: !entrant });
  };

  const openNewEntrant = () => {
    setPickerOpen(false);
    openEntrant('new');
  };

  // The row the URL names. An id nobody here has — a stale link, an entry that
  // was withdrawn — simply closes the form rather than opening it blank.
  const editing = entrantParam && entrantParam !== 'new'
    ? registrations.find((r) => r.id === entrantParam) ?? null
    : null;
  const entrantDialogOpen = entrantParam === 'new' || !!editing;

  const handleEntrantSaved = (saved: Registration) => {
    openEntrant(null);
    const known = registrations.some((r) => r.id === saved.id);
    refreshCounts(
      known ? registrations.map((r) => (r.id === saved.id ? saved : r)) : [...registrations, saved]
    );
    // A candidate is somebody's entry at another stage, matched by address —
    // once they are in here, they are no longer somebody to carry forward.
    setCandidates((current) =>
      current.filter((c) => c.email?.toLowerCase() !== saved.email?.toLowerCase())
    );
  };

  const handleEntrantDeleted = (removed: Registration) => {
    openEntrant(null);
    refreshCounts(registrations.filter((r) => r.id !== removed.id));
  };

  // The file touched some rows and added others; both come back and are
  // merged in place, so the list does not jump while the desk is reading it.
  const handleImported = (result: ImportResult) => {
    const byId = new Map(result.registrations.map((r) => [r.id, r]));
    const kept = registrations.map((r) => byId.get(r.id) ?? r);
    const known = new Set(registrations.map((r) => r.id));
    const added = result.registrations.filter((r) => !known.has(r.id));
    refreshCounts([...kept, ...added]);
    const emails = new Set(result.registrations.map((r) => r.email?.toLowerCase()));
    setCandidates((current) => current.filter((c) => !emails.has(c.email?.toLowerCase())));
  };

  // Read straight off the loaded list: the export is a file of what the desk
  // is looking at, not a second request for it.
  const handleExport = async () => {
    if (!slug || exporting) return;
    setExporting(true);
    try {
      await downloadEntries(registrations, slug, t, { payments: acceptsPayments });
    } catch (error) {
      toast({
        title: t('errors.unexpectedError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const summary = useMemo(
    () =>
      t(acceptsPayments ? 'gymAdmin.registrations.summary' : 'gymAdmin.registrations.summaryNoPayments', {
        total: registrations.length,
        paid: paidCount,
        confirmed: confirmedCount,
      }),
    [registrations.length, paidCount, confirmedCount, acceptsPayments, t]
  );

  /**
   * The tables, in the order the desk reads them. Every category the
   * competition runs gets one, empty or not — they are the shape of the day.
   * The unsorted group is different: it is a to-do list, so it shows while
   * anybody is in it and disappears once everyone has a category (or stands
   * alone at a competition that runs none).
   */
  const turnToChooseCount = useMemo(
    () => registrations.filter((r) => r.turn_to_choose).length,
    [registrations]
  );
  // A filter with nothing left in it switches itself off, so the list does not
  // stay empty once the last turn is booked.
  const filteringTurns = turnFilter && turnToChooseCount > 0;

  const visible = useMemo(
    () =>
      registrations.filter(
        (r) =>
          (teamFilter === ALL_TEAMS || (r.team_id ?? NO_TEAM) === teamFilter) &&
          (!filteringTurns || r.turn_to_choose)
      ),
    [registrations, teamFilter, filteringTurns]
  );

  const groups = useMemo(() => {
    const unsorted = visible.filter((r) => !r.category_id);
    const head =
      unsorted.length > 0 || categories.length === 0
        ? [{ id: null, title: t('gymAdmin.registrations.noCategory'), rows: unsorted }]
        : [];

    const all = [
      ...head,
      ...categories.map((category) => ({
        id: category.id,
        title: category.name,
        rows: visible.filter((r) => r.category_id === category.id),
      })),
    ];
    // Narrowed to the turns to choose, only the classes that have one.
    return filteringTurns ? all.filter((group) => group.rows.length > 0) : all;
  }, [visible, categories, filteringTurns, t]);

  const showTurnFilter = turnsOffered && turnToChooseCount > 0;
  const showFilters = teams.length > 0 || showTurnFilter;

  if (stageLoading) return <Skeleton className="h-96 w-full max-w-6xl" />;
  if (!stage || !slug) return null;

  if (dpaRequired) {
    return (
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="size-5 text-destructive" />
            {t('gymAdmin.registrations.dpaTitle')}
          </CardTitle>
          {/* Whose signature is missing: the circuit's at a stage, the hosting
              gym's at its own event. */}
          <CardDescription>
            {t(place.stage ? 'gymAdmin.registrations.dpaDescription' : 'gymAdmin.registrations.dpaDescriptionGym')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>{t('gymAdmin.registrations.title')}</CardTitle>
            <CardDescription>{summary}</CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {/* The file is the list as it stands: exporting is reading, and
                stays open when the desk has closed. */}
            <Button
              variant="outline"
              className="gap-2"
              disabled={registrations.length === 0 || exporting}
              onClick={handleExport}
            >
              <Download className="size-4" />
              {t('gymAdmin.registrations.export')}
            </Button>
            {deskOpen && (
              <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
                <Upload className="size-4" />
                {t('gymAdmin.registrations.import')}
              </Button>
            )}
            {deskOpen && (
              <Button onClick={() => setPickerOpen(true)} className="gap-2">
                <Plus className="size-4" />
                {t('gymAdmin.registrations.add')}
              </Button>
            )}
          </div>
        </CardHeader>
        {!deskOpen && (
          <CardContent>
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              {t('gymAdmin.registrations.deskClosed')}
            </p>
          </CardContent>
        )}
        {showFilters && (
          <CardContent
            className={`flex flex-col gap-2 sm:flex-row sm:items-center ${deskOpen ? '' : 'pt-0'}`}
          >
            {teams.length > 0 && (
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger
                  className="w-full sm:w-64"
                  aria-label={t('gymAdmin.registrations.teamFilter')}
                  data-testid="registrations-team-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TEAMS}>{t('gymAdmin.registrations.allTeams')}</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NO_TEAM}>{t('gymAdmin.registrations.noTeam')}</SelectItem>
                </SelectContent>
              </Select>
            )}
            {/* Who still has a turn to choose, on a class split over several:
                they are on every turn's board until somebody books one. */}
            {showTurnFilter && (
              <Button
                type="button"
                variant={filteringTurns ? 'secondary' : 'outline'}
                aria-pressed={filteringTurns}
                className="gap-2"
                onClick={() => setTurnFilter((current) => !current)}
                data-testid="registrations-turn-filter"
              >
                <CalendarClock className="size-4" />
                {t('gymAdmin.registrations.turnToChooseFilter', { count: turnToChooseCount })}
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {listLoading && <Skeleton className="h-48 w-full" />}

      {!listLoading && registrations.length === 0 && categories.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('gymAdmin.registrations.empty')}
        </p>
      )}

      {!listLoading &&
        (registrations.length > 0 || categories.length > 0) &&
        groups.map((group) => (
          <RegistrationsTable
            key={group.id ?? 'none'}
            categoryId={group.id}
            title={group.title}
            registrations={group.rows}
            categories={categories}
            teams={teams}
            deskOpen={deskOpen}
            showGender={genderCollected}
            showTurn={turnsOffered}
            showPaid={acceptsPayments}
            emptyLabel={t('gymAdmin.registrations.emptyGroup')}
            onBulk={applyBulk}
            onTogglePaid={togglePaid}
            onToggleConfirmed={toggleConfirmed}
            onEdit={(registration) => openEntrant(registration.id)}
          />
        ))}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.registrations.add')}</DialogTitle>
            <DialogDescription>{t('gymAdmin.registrations.addDescription')}</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('gymAdmin.registrations.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          <Button variant="outline" onClick={openNewEntrant} className="w-full gap-2">
            <UserPlus className="size-4" />
            {t('gymAdmin.registrations.newEntrant')}
          </Button>

          <div className="max-h-72 space-y-1 overflow-y-auto">
            {candidatesLoading && <Skeleton className="h-12 w-full" />}

            {!candidatesLoading && candidates.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('gymAdmin.registrations.noCandidates')}
              </p>
            )}

            {!candidatesLoading &&
              candidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => handleAdd(candidate)}
                  data-testid="registration-candidate"
                  className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{candidate.full_name}</span>
                    {candidate.email && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {candidate.email}
                      </span>
                    )}
                  </span>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <EntrantDialog
        open={entrantDialogOpen}
        onOpenChange={(open) => {
          if (!open) openEntrant(null);
        }}
        stageSlug={slug}
        registration={editing}
        categories={categories}
        teams={teams}
        onTeamCreated={addTeam}
        deskOpen={deskOpen}
        genderCollected={genderCollected}
        assignsCategories={assignsCategories}
        onSaved={handleEntrantSaved}
        onDeleted={handleEntrantDeleted}
      />

      <ImportEntriesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        stageSlug={slug}
        registrations={registrations}
        categories={categories}
        teams={teams}
        genderCollected={genderCollected}
        turnsOffered={turnsOffered}
        acceptsPayments={acceptsPayments}
        onImported={handleImported}
      />
    </div>
  );
}
